import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loggingApiService, UserActivity } from '../../../../../services/loggingApiService';
import { DocumentVersion } from '../types/documentVersion.types';
import { formatVersionLabel } from '../utils/versionUtils';

interface DocumentTrackingPanelProps {
  documentId: string;
  documentTitle?: string;
  versions: DocumentVersion[];
}

type EventFilter = 'all' | 'document_access' | 'document_update' | 'document_version_creation' | 'document_version_access' | 'email_send' | 'email_attachment_send';

const EVENT_LABELS: Record<EventFilter, string> = {
  all: 'All Events',
  document_access: 'Opened',
  document_update: 'Edited',
  document_version_creation: 'Version Created',
  document_version_access: 'Version Viewed',
  email_send: 'Email Sent',
  email_attachment_send: 'Email Attachment Sent',
};

const ALLOWED_CATEGORIES = new Set(['document_management', 'email_management']);

const getEventBadgeClasses = (activityType: string) => {
  switch (activityType) {
    case 'document_update':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'document_version_creation':
      return 'bg-lex-accent-soft text-lex-accent';
    case 'email_send':
    case 'email_attachment_send':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    default:
      return 'bg-lex-accent-soft text-lex-accent';
  }
};

const formatRecipientList = (recipients?: Array<{ name?: string; email: string }>) => {
  if (!recipients || recipients.length === 0) return 'None';
  const formatted = recipients.map((r) => r.name ? `${r.name} <${r.email}>` : r.email);
  if (formatted.length <= 3) return formatted.join(', ');
  return `${formatted.slice(0, 3).join(', ')} +${formatted.length - 3} more`;
};

interface AttachmentSummary {
  attachment_id?: string;
  filename?: string;
  document_title?: string;
  document_version_label?: string;
  document_version_id?: string;
  document_checksum_sha256?: string;
}

export const DocumentTrackingPanel: React.FC<DocumentTrackingPanelProps> = ({ documentId, documentTitle, versions }) => {
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('all');
  const [selectedEvent, setSelectedEvent] = useState<EventFilter>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const versionMap = useMemo(() => {
    return new Map(versions.map((version) => [version.version_id, version]));
  }, [versions]);

  const fetchActivities = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    try {
      const metadata: Record<string, string> = { document_id: documentId };
      if (selectedVersionId !== 'all') {
        metadata.document_version_id = selectedVersionId;
      }
      const data = await loggingApiService.getUserActivities({
        metadata,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit: 200,
        page: 1,
      });
      const filtered = data.activities.filter((activity) => ALLOWED_CATEGORIES.has(activity.activityCategory));
      setActivities(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracking events');
    } finally {
      setLoading(false);
    }
  }, [documentId, selectedVersionId, startDate, endDate]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const filteredActivities = useMemo(() => {
    let filtered = activities.filter((activity) => ALLOWED_CATEGORIES.has(activity.activityCategory));

    filtered = filtered.filter((activity) => {
      if (activity.activityCategory !== 'document_management') return true;
      if (activity.activityType !== 'document_access' && activity.activityType !== 'document_version_access') {
        return true;
      }
      const endpoint = activity.metadata?.endpoint as string | undefined;
      return endpoint ? endpoint.includes('/access-track') : false;
    });

    if (selectedEvent !== 'all') {
      filtered = filtered.filter((activity) => activity.activityType === selectedEvent);
    }

    const seen = new Set<string>();
    const deduped: UserActivity[] = [];
    for (const activity of filtered) {
      const timestampKey = new Date(activity.timestamp).toISOString().slice(0, 19);
      const key = [
        activity.activityType,
        activity.userId,
        activity.metadata?.document_id ?? '',
        activity.metadata?.document_version_id ?? '',
        timestampKey,
      ].join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(activity);
    }
    return deduped;
  }, [activities, selectedEvent]);

  const renderEventDetails = (activity: UserActivity) => {
    const metadata = activity.metadata || {};
    const recipients = metadata.recipients || {};
    const versionId = metadata.document_version_id as string | undefined;
    const versionInfo = versionId ? versionMap.get(versionId) : undefined;
    const versionLabel = versionInfo?.version_number ? formatVersionLabel(versionInfo.version_number) : undefined;
    const documentTitleValue = (metadata.document_title as string | undefined) || documentTitle;
    const documentIdValue = (metadata.document_id as string | undefined) || documentId;
    const mailboxEmail = metadata.mailbox_email as string | undefined;

    const attachmentList = Array.isArray(metadata.attachments)
      ? (metadata.attachments as AttachmentSummary[])
      : [];

    const resolveAttachmentVersionLabel = (attachment: AttachmentSummary) => {
      if (attachment.document_version_label) {
        return attachment.document_version_label;
      }
      if (attachment.document_version_id) {
        const linkedVersion = versionMap.get(attachment.document_version_id);
        if (linkedVersion?.version_number) {
          return formatVersionLabel(linkedVersion.version_number);
        }
      }
      return '—';
    };

    const resolveAttachmentName = (attachment: AttachmentSummary) => {
      return attachment.document_title || attachment.filename || 'Untitled attachment';
    };

    const renderAttachments = () => (
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Attachments</div>
        {attachmentList.length === 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">None</div>
        )}
        {attachmentList.map((attachment, index) => (
          <div
            key={attachment.attachment_id || `${attachment.filename || 'attachment'}-${index}`}
            className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 px-3 py-2"
          >
            <div className="text-sm text-gray-800 dark:text-gray-200">
              Name: {resolveAttachmentName(attachment)}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Version: {resolveAttachmentVersionLabel(attachment)}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 break-all">
              Hash: {attachment.document_checksum_sha256 || '—'}
            </div>
          </div>
        ))}
      </div>
    );

    if (activity.activityCategory === 'email_management') {
      const toLine = formatRecipientList(recipients.to);
      const ccLine = formatRecipientList(recipients.cc);
      const bccLine = formatRecipientList(recipients.bcc);
      return (
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {(documentTitleValue || documentIdValue) && (
            <div className="space-y-0.5">
              {documentTitleValue && <div className="truncate">Document: {documentTitleValue}</div>}
              {documentIdValue && <TechnicalRecordDetails documentId={documentIdValue} />}
            </div>
          )}
          <div>To: {toLine}</div>
          <div>CC: {ccLine}</div>
          <div>BCC: {bccLine}</div>
          {mailboxEmail && <div>Sent From: {mailboxEmail}</div>}
          {metadata.subject && <div className="truncate">Subject: {metadata.subject}</div>}
          {metadata.message_id_header && (
            <div className="text-xs text-gray-500 dark:text-gray-400">Message-ID: {metadata.message_id_header}</div>
          )}
          {renderAttachments()}
        </div>
      );
    }

    return (
      <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
        {documentTitleValue && <div className="truncate">Document: {documentTitleValue}</div>}
        {documentIdValue && <TechnicalRecordDetails documentId={documentIdValue} />}
        {versionLabel && <div>Version: {versionLabel}</div>}
      </div>
    );
  };

  const getEventLabel = (activity: UserActivity) => {
    const baseLabel = EVENT_LABELS[activity.activityType as EventFilter] || activity.activityType;
    if (activity.userName) {
      return `${baseLabel} by ${activity.userName}`;
    }
    return baseLabel;
  };

  return (
    <div className="mt-10 min-w-0 max-w-full space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Tracking</h4>
        <button
          onClick={fetchActivities}
          className="text-xs font-medium text-lex-accent hover:text-lex-accent-hover"
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)]">
        <div className="min-w-0 space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Version</label>
          <select
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            value={selectedVersionId}
            onChange={(event) => setSelectedVersionId(event.target.value)}
          >
            <option value="all">All Versions</option>
            {versions.map((version) => (
              <option key={version.version_id} value={version.version_id}>
                {formatVersionLabel(version.version_number)}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0 space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Event Type</label>
          <select
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            value={selectedEvent}
            onChange={(event) => setSelectedEvent(event.target.value as EventFilter)}
          >
            {Object.entries(EVENT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0 space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Date Range</label>
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
            <input
              type="date"
              className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
            <input
              type="date"
              className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading tracking events…</div>
      )}
      {!loading && error && (
        <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
      )}
      {!loading && !error && filteredActivities.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400">
          No tracking events found for the selected filters.
        </div>
      )}

      {!loading && !error && filteredActivities.length > 0 && (
        <div className="min-w-0 space-y-3">
          {filteredActivities.map((activity) => (
            <div
              key={activity.id}
              className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/40"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className={`rounded-full px-2 py-0.5 font-semibold ${getEventBadgeClasses(activity.activityType)}`}>
                  {getEventLabel(activity)}
                </span>
                <span>{new Date(activity.timestamp).toLocaleString()}</span>
              </div>
              <div className="mt-3">
                {renderEventDetails(activity)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TechnicalRecordDetails = ({ documentId }: { documentId: string }) => (
  <details className="text-xs text-gray-500 dark:text-gray-400">
    <summary className="cursor-pointer select-none">
      DMS record ending {documentId.slice(-8)}
    </summary>
    <div className="mt-1 break-all rounded bg-gray-50 px-2 py-1 font-mono dark:bg-gray-900/70">
      {documentId}
    </div>
  </details>
);
